/**
 * PresenceDot — Teams-style colored dot for Online / Away / Offline status.
 * Sizes: sm (12px), md (16px). Pass `withLabel` for inline text label.
 *
 *   <PresenceDot status="online" />
 *   <PresenceDot status="away" withLabel />
 */
const TONE = {
  online: {
    dot: "bg-green-500",
    ring: "ring-green-300 dark:ring-green-400/70",
    glow: "shadow-[0_0_8px_2px_rgba(34,197,94,0.85)]",
    label: "Online",
    text: "text-green-600 dark:text-green-400",
  },
  away: {
    dot: "bg-yellow-400",
    ring: "ring-yellow-300 dark:ring-yellow-300/70",
    glow: "shadow-[0_0_8px_2px_rgba(250,204,21,0.85)]",
    label: "Away",
    text: "text-yellow-600 dark:text-yellow-300",
  },
  offline: {
    dot: "bg-slate-300 dark:bg-slate-600",
    ring: "ring-slate-200 dark:ring-slate-700/60",
    glow: "",
    label: "Offline",
    text: "text-slate-400",
  },
};

export default function PresenceDot({ status = "offline", size = "sm", withLabel = false, lastSeenAt = null, className = "" }) {
  const tone = TONE[status] || TONE.offline;
  const dotSize = size === "md" ? "h-4 w-4" : "h-3 w-3";
  const isLive = status === "online" || status === "away";
  const ringClass = status === "online" ? `${tone.ring} animate-pulse` : tone.ring;
  const title = lastSeenAt && status !== "online"
    ? `${tone.label} — last seen ${new Date(lastSeenAt).toLocaleString()}`
    : tone.label;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} title={title} data-testid={`presence-${status}`}>
      <span className={`relative inline-flex ${dotSize}`}>
        <span className={`absolute inset-0 rounded-full ring-2 ring-white dark:ring-slate-900 ${tone.dot} ${isLive ? tone.glow : ""}`} />
        {isLive && (
          <span className={`absolute inset-0 rounded-full ring-2 ${ringClass}`} />
        )}
      </span>
      {withLabel && (
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${tone.text}`}>{tone.label}</span>
      )}
    </span>
  );
}
